import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { createGroundPlate, createSolarRig, type SolarSceneState } from '../solar/solarRig';
import {
  boundingRectMask, extractWallMask, footprintMask, loadFloorPlanImage, luminanceMask, mirrorMask, openingsMask, rasterizeFloorPlan, roomRegions,
  suggestCrop, wallRects, DEFAULT_WALL_THRESHOLD, SHEET_ANALYSIS_SIDE, WALL_ANALYSIS_SIDE, type CropRect, type Mask, type WallRect,
} from './floorPlanWalls';

export type FloorStyle = 'rendered' | 'drawing';

export interface FloorPlanModel {
  imageUrl: string;
  /** Percent rectangle of the sheet to show; null = auto-detect the unit outline. */
  crop: CropRect | null;
  mirror: boolean;
  /** Wall height as a percent of the plate width; null = default. */
  wallHeightPercent: number | null;
  /** Ink luminance threshold for wall detection (0–255); null = automatic. */
  wallThreshold?: number | null;
  /** `rendered` = timber floor under the unit footprint with glass openings; `drawing` = the sheet itself as the floor. */
  floorStyle?: FloorStyle;
}

export const DEFAULT_WALL_HEIGHT_PERCENT = 6;

export interface FloorPlanWallsInfo { spans: number; rooms: number; crop: CropRect | null; suggestedCrop: CropRect | null; footprintFound: boolean }

/** Procedural timber planks (no external asset; artifacts must stay self-contained). */
function plankTexture(): THREE.CanvasTexture {
  const size = 512, plankH = 64;
  const canvas = document.createElement('canvas'); canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  let seed = 7;
  const rand = () => { seed = (seed * 16807) % 2147483647; return seed / 2147483647; };
  for (let row = 0; row < size / plankH; row++) {
    let x = -Math.floor(rand() * 200);
    while (x < size) {
      const len = 160 + Math.floor(rand() * 220);
      const tone = 0.86 + rand() * 0.14;
      ctx.fillStyle = `rgb(${Math.round(196 * tone)}, ${Math.round(158 * tone)}, ${Math.round(116 * tone)})`;
      ctx.fillRect(x, row * plankH, len, plankH);
      // grain
      ctx.strokeStyle = `rgba(90, 60, 30, ${0.05 + rand() * 0.06})`; ctx.lineWidth = 1;
      for (let g = 0; g < 6; g++) {
        const gy = row * plankH + 6 + rand() * (plankH - 12);
        ctx.beginPath(); ctx.moveTo(x + 4, gy); ctx.bezierCurveTo(x + len * 0.3, gy + rand() * 6 - 3, x + len * 0.7, gy + rand() * 6 - 3, x + len - 4, gy); ctx.stroke();
      }
      // plank edge
      ctx.fillStyle = 'rgba(70, 45, 25, 0.28)'; ctx.fillRect(x, row * plankH, 2, plankH); ctx.fillRect(x, row * plankH, len, 2);
      x += len;
    }
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping; texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

/** Boxes for every rect on a mask grid, base at y=0, merged into one geometry. */
function rectsGeometry(rects: WallRect[], mask: Mask, width: number, depth: number, height: number, overlap = 0.2,
  colorFor?: (faceIndex: number) => [number, number, number]): THREE.BufferGeometry | null {
  const cellW = width / mask.width, cellD = depth / mask.height;
  const geometries = rects.map(rect => {
    const box = new THREE.BoxGeometry(rect.width * cellW + cellW * overlap, height, rect.height * cellD + cellD * overlap);
    box.translate((rect.x + rect.width / 2) * cellW - width / 2, height / 2, (rect.y + rect.height / 2) * cellD - depth / 2);
    if (colorFor) {
      // BoxGeometry lays out 4 vertices per face in the order +x, -x, +y, -y, +z, -z.
      const colors = new Float32Array(24 * 3);
      for (let v = 0; v < 24; v++) colors.set(colorFor(Math.floor(v / 4)), v * 3);
      box.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    }
    return box;
  });
  const merged = geometries.length ? mergeGeometries(geometries, false) : null;
  geometries.forEach(g => g.dispose());
  return merged;
}

/** Planar UVs from world XZ so plank texture runs continuously across every box. */
function planarUVs(geometry: THREE.BufferGeometry, width: number, depth: number, repeat: number) {
  const position = geometry.getAttribute('position');
  const uv = new Float32Array(position.count * 2);
  for (let i = 0; i < position.count; i++) {
    uv[i * 2] = (position.getX(i) / width + 0.5) * repeat;
    uv[i * 2 + 1] = (position.getZ(i) / depth + 0.5) * repeat * depth / width;
  }
  geometry.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
}

/**
 * Floor-plan solar scene: the shared rig, a plate textured with the cropped
 * sheet, and walls extruded from the image's thick strokes. Walls rise with
 * the entrance reveal; the plate flips horizontally for mirrored stacks.
 * In the default `rendered` style the paper is hidden and the unit stands as
 * a dollhouse: timber floor under its footprint, white walls with grey tops,
 * glass where the footprint edge has no wall, on a shadow-only ground.
 * `updateModel` swaps crop / mirror / wall height / style without recreating
 * the WebGL context (the admin calibration preview drives it while dragging).
 */
export function createFloorPlanScene(host: HTMLDivElement, initial: FloorPlanModel, state: () => SolarSceneState,
  onTime: (minutes: number) => void, onPlaybackLoop: () => void, onError: (message: string) => void,
  onWalls?: (info: FloorPlanWallsInfo) => void) {
  const rig = createSolarRig(host, { state, onTime, onPlaybackLoop, onError });
  let model: FloorPlanModel = { floorStyle: 'rendered', ...initial };
  let plate: ReturnType<typeof createGroundPlate> | null = null;
  let plateReady = false;
  let plateToken = 0;
  let image: HTMLImageElement | null = null;
  let suggestedCrop: CropRect | null = null;
  let lastSpans = 0;
  const built = new THREE.Group(); rig.site.add(built);
  const wallMaterial = new THREE.MeshStandardMaterial({ color: '#ffffff', vertexColors: true, roughness: 0.82, metalness: 0.02 });
  const wood = plankTexture(); rig.textures.add(wood);
  const floorMaterial = new THREE.MeshStandardMaterial({ map: wood, roughness: 0.7, metalness: 0.03 });
  const glassMaterial = new THREE.MeshPhysicalMaterial({ color: '#cfe6ff', transparent: true, opacity: 0.32, roughness: 0.08, metalness: 0.1,
    transmission: 0, side: THREE.DoubleSide, depthWrite: false });
  // Shadow-only ground so the dollhouse floats on the dark stage yet still casts a contact shadow.
  const shadowGround = new THREE.Mesh(new THREE.PlaneGeometry(600, 600), new THREE.ShadowMaterial({ opacity: 0.38 }));
  shadowGround.rotation.x = -Math.PI / 2; shadowGround.position.y = -0.02; shadowGround.receiveShadow = true; rig.site.add(shadowGround);
  const effectiveCrop = () => model.crop ?? suggestedCrop;
  const wallHeight = () => (plate?.width ?? 120) * ((model.wallHeightPercent ?? DEFAULT_WALL_HEIGHT_PERCENT) / 100);
  const rendered = () => (model.floorStyle ?? 'rendered') === 'rendered';

  function clearBuilt() {
    built.children.forEach(child => (child as THREE.Mesh).geometry?.dispose());
    built.clear();
  }
  function applyStyle() {
    // With nothing detected there is nothing to render, so fall back to the sheet.
    const show = rendered() && lastSpans > 0;
    if (plate) { plate.ground.visible = !show; plate.plinth.visible = !show; }
    built.children.forEach(child => { if (child.userData.renderedOnly) child.visible = show; });
  }
  function buildWalls() {
    if (!plate || !plateReady || !image || rig.disposed) return;
    clearBuilt();
    const crop = effectiveCrop();
    // Walls are detected on the cropped unit rasterised at full analysis
    // resolution: on the whole sheet they would be only a pixel or two wide
    // and indistinguishable from text.
    const pixels = rasterizeFloorPlan(image, WALL_ANALYSIS_SIDE, crop);
    const threshold = model.wallThreshold ?? DEFAULT_WALL_THRESHOLD;
    let ink = luminanceMask(pixels.rgba, pixels.width, pixels.height, threshold);
    let walls = extractWallMask(pixels.rgba, pixels.width, pixels.height, { threshold });
    if (model.mirror) { ink = mirrorMask(ink); walls = mirrorMask(walls); }
    const width = plate.width, depth = plate.depth, height = wallHeight();
    const enclosed = footprintMask(ink);
    const footprint = enclosed ?? boundingRectMask(walls, Math.round(pixels.width * 0.01));
    const footprintFound = enclosed != null;
    const rects = wallRects(walls);
    lastSpans = rects.length;

    // Walls: white sides, light-grey tops (face 2 is +y in BoxGeometry).
    const wallGeometry = rectsGeometry(rects, walls, width, depth, height, 0.2,
      face => (face === 2 ? [0.80, 0.83, 0.86] : [0.96, 0.97, 0.98]));
    if (wallGeometry) {
      const mesh = new THREE.Mesh(wallGeometry, wallMaterial); mesh.castShadow = true; mesh.receiveShadow = true;
      mesh.userData.reveal = true; built.add(mesh);
    }
    let rooms = 0;
    if (footprint) {
      // Floor slab under the whole footprint (walls stand on it).
      const slab = height * 0.08;
      const floorGeometry = rectsGeometry(wallRects(footprint), footprint, width, depth, slab, 0.05);
      if (floorGeometry) {
        planarUVs(floorGeometry, width, depth, 7);
        const mesh = new THREE.Mesh(floorGeometry, floorMaterial); mesh.receiveShadow = true; mesh.castShadow = true;
        mesh.userData.renderedOnly = true; built.add(mesh);
      }
      // Glass where the footprint edge has no wall: windows, balcony fronts, doors.
      const openings = openingsMask(footprint, walls, Math.max(2, Math.round(pixels.width * 0.004)));
      const glassGeometry = rectsGeometry(wallRects(openings), openings, width, depth, height * 0.92, 0);
      if (glassGeometry) {
        const mesh = new THREE.Mesh(glassGeometry, glassMaterial); mesh.position.y = slab;
        mesh.userData.reveal = true; mesh.userData.renderedOnly = true; built.add(mesh);
      }
      rooms = roomRegions(footprint, walls, Math.round(pixels.width * pixels.height * 0.002)).rooms.length;
    }
    applyStyle();
    onWalls?.({ spans: rects.length, rooms, crop, suggestedCrop, footprintFound });
  }
  function startPlate() {
    if (plate) { rig.site.remove(plate.ground, plate.plinth); plate.ground.geometry.dispose(); plate.plinth.geometry.dispose(); }
    plateReady = false;
    const token = ++plateToken;
    plate = createGroundPlate(rig, model.imageUrl, { crop: effectiveCrop(), mirror: model.mirror, onError,
      onReady: () => { if (token !== plateToken) return; plateReady = true; buildWalls(); } });
    applyStyle();
  }
  function refreshSuggestedCrop() {
    if (!image) return;
    const sheet = rasterizeFloorPlan(image, SHEET_ANALYSIS_SIDE);
    suggestedCrop = suggestCrop(extractWallMask(sheet.rgba, sheet.width, sheet.height, { threshold: model.wallThreshold ?? null }));
  }

  // Pixels and texture load in parallel; whichever finishes last builds the walls.
  loadFloorPlanImage(model.imageUrl).then(loaded => {
    if (rig.disposed) return;
    image = loaded;
    refreshSuggestedCrop();
    // The plate started uncropped; restart it on the detected outline.
    if (!model.crop && suggestedCrop) startPlate(); else buildWalls();
  }).catch(error => { if (!rig.disposed) onError(error instanceof Error ? error.message : 'Could not read the floor plan image.'); });
  startPlate();

  rig.onTick(({ reveal }) => {
    const progress = reveal(0);
    built.children.forEach(child => {
      if (!child.userData.reveal) return;
      child.scale.y = Math.max(0.0001, progress);
      if (progress <= 0) child.visible = false;
      else if (child.userData.renderedOnly) child.visible = rendered();
      else child.visible = true;
    });
  });
  rig.onDispose(() => { clearBuilt(); wallMaterial.dispose(); floorMaterial.dispose(); glassMaterial.dispose(); shadowGround.geometry.dispose(); (shadowGround.material as THREE.Material).dispose(); });
  return {
    reset: rig.reset, zoom: rig.zoom, dispose: rig.dispose,
    updateModel: (next: Omit<FloorPlanModel, 'imageUrl'>) => {
      const cropChanged = JSON.stringify(next.crop ?? null) !== JSON.stringify(model.crop ?? null);
      const mirrorChanged = next.mirror !== model.mirror;
      const thresholdChanged = (next.wallThreshold ?? null) !== (model.wallThreshold ?? null);
      const heightChanged = (next.wallHeightPercent ?? null) !== (model.wallHeightPercent ?? null);
      model = { ...model, ...next };
      if (thresholdChanged && !model.crop) refreshSuggestedCrop();
      if (cropChanged || mirrorChanged || (thresholdChanged && !model.crop)) startPlate();
      else if (thresholdChanged || heightChanged) buildWalls();
      else applyStyle();
    },
  };
}
